import { AuthEndpoint as AuthEndpointInterface } from "@/projectRoot/api-client/interfaces/endpoints/Auth.endpoint";
import ApiResponseDto from "@apiClient/interfaces/response/ApiResponse.dto";
import { KyInstance } from "ky/distribution/types/ky";
import { KyResponse } from "ky";
import DeviceTokenGenerateRequestDto from "@/projectRoot/api-client/interfaces/request/auth/DeviceTokenGenerateRequest.dto";
import DeviceTokenGenerateResponseDto from "@/projectRoot/api-client/interfaces/response/auth/DeviceTokenGenerateResponse.dto";
import CustomerDto from "@/projectRoot/api-client/interfaces/models/customers/Customer.dto";
import LoginRequestDto from "@/projectRoot/api-client/interfaces/request/auth/LoginRequest.dto";
import RegistrationRequestDto from "@/projectRoot/api-client/interfaces/request/auth/RegistrationRequest.dto";
import UpdateRequestDto from "@/projectRoot/api-client/interfaces/request/auth/UpdateRequest.dto";
import ChangePasswordRequestDto from "@/projectRoot/api-client/interfaces/request/auth/ChangePasswordRequest.dto";
import InitResetPasswordRequestDto from "@/projectRoot/api-client/interfaces/request/auth/InitResetPasswordRequest.dto";
import ResetPasswordRequestDto from "@/projectRoot/api-client/interfaces/request/auth/ResetPasswordRequest.dto";
import VerifyResetTokenRequestDto from "@/projectRoot/api-client/interfaces/request/auth/VerifyResetTokenRequest.dto";
import UpdateAddressRequestDto from "@/projectRoot/api-client/interfaces/request/auth/UpdateAddressRequest.dto";
import LoyaltyTransactionDto from "@/projectRoot/api-client/interfaces/models/customers/loyalty/LoyaltyTransaction.dto";
import ProductDto from "@/projectRoot/api-client/interfaces/models/products/Product.dto";
import UpdateWishlistRequestDto from "@/projectRoot/api-client/interfaces/request/auth/UpdateWishlistRequest.dto";

export class AuthEndpoint implements AuthEndpointInterface {

    private _httpClient: KyInstance;
    private cache: RequestCache;

    public constructor(client: KyInstance, cache: RequestCache = "no-store") {
        this._httpClient = client;
        this.cache = cache;
    }

    get httpClient(): KyInstance {
        if (!this._httpClient) {
            throw new Error('Ky http client is not initialized');
        }
        return this._httpClient;
    }

    set httpClient(kyInstance: KyInstance) {
        this._httpClient = kyInstance;
    }

    /**
     * /api/auth/device-token/generate
     */
    async deviceTokenGenerate(params: DeviceTokenGenerateRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/device-token/generate`, { json: params, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/socialite/{driver}
     */
    async socialLogin(driver: string): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .get(`auth/${driver}`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/identity
     */
    async identity(): Promise<ApiResponseDto<CustomerDto, undefined>> {
        return this._httpClient
            .get(`auth/identity`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<CustomerDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/login
     */
    async login(request: LoginRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/login`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/register
     */
    async register(request: RegistrationRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/register`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/update
     */
    async update(request: UpdateRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/update`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/create-address
     */
    async createAddress(request: UpdateAddressRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/create-address`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/update-address
     */
    async updateAddress(request: UpdateAddressRequestDto,uid:string): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/update-address/` + uid, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/delete-address
     */
    async deleteAddress(uid:string): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/delete-address/` + uid, { cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }
    
    /**
     * /api/auth/change-password
     */
    async changePassword(request: ChangePasswordRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`auth/change-password`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /*    
        * /api/auth/customers/loyalty/transactions
     */
    async getLoyaltyTransactions(): Promise<ApiResponseDto<LoyaltyTransactionDto[], undefined>> {
        return this._httpClient
            .get(`auth/customers/loyalty/transactions`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<LoyaltyTransactionDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/init-reset-password
     */
    async initResetPassword(request: InitResetPasswordRequestDto): Promise<ApiResponseDto<[], undefined>> {
        return this._httpClient
            .post(`auth/init-reset-password`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<[], undefined> = await kyResponse.json();
                return response;
            });
    }

     /**
     * /api/auth/verify-reset-token
     */
    async verifyResetToken(request: VerifyResetTokenRequestDto): Promise<ApiResponseDto<[], undefined>> {
        return this._httpClient
            .post(`auth/verify-reset-token`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/auth/reset-password
     */
    async resetPassword(request: ResetPasswordRequestDto): Promise<ApiResponseDto<[], undefined>> {
        return this._httpClient
            .post(`auth/reset-password`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/wishlist
     */
    async wishlist(): Promise<ApiResponseDto<ProductDto[], undefined>> {
        return this._httpClient
            .get(`wishlist`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<ProductDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/update-wishlist
     */
    async updateWishlist(request: UpdateWishlistRequestDto): Promise<ApiResponseDto<CustomerDto, undefined>> {
        return this._httpClient
            .post(`update-wishlist`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<CustomerDto, undefined> = await kyResponse.json();
                return response;
            });
    }
}

export default AuthEndpoint;